// Map catalog: fetches the padpork map list (via our server proxy) and
// provides search, random selection (with keywords), and favorites.

import { api } from "./base";

export interface MapInfo {
  map_name: string;
  normalized_name: string;
  game: string;
  longname: string | null;
  author: string | null;
  has_rocket_launcher: number;
  has_plasmagun: number;
  has_grenade_launcher: number;
  has_slick: number;
  has_water: number;
  has_slime: number;
  has_lava: number;
  has_fog: number;
  has_teleporter: number;
  has_jumppad: number;
  has_timer: number;
  record_count: number;
  best_time_ms: number | null;
  best_player_name: string | null;
  /** Present for maps inside a map pack (`maps/<pack>/`). */
  pack?: string;
  pack_name?: string;
  pack_description?: string;
}

interface Catalog {
  total: number;
  items: MapInfo[];
}

// Bumped when the *server-side* catalog gains new content, so stale
// localStorage caches (7-day TTL) don't hide it — v2 added the local Quake Live
// rows, v3 the drop-in pk3 maps (e.g. proverek). Bump this when maps are added.
const CATALOG_KEY = "webrace.catalog.v5";
const CATALOG_TS_KEY = "webrace.catalog.v5.ts";
const FAVORITES_KEY = "webrace.favorites";

/**
 * Maps we actively test on. Always shown as favorites under the "testing"
 * category for every user; keep this list updated as new test maps are added.
 */
export const TESTING_MAPS: string[] = [
  "AMT-Freestyle2",
  "Lick-Freestyle",
  "elco-backside-wjfix",
  "ump1ctf1",
  "BardoK-Strafe1",
  "cyfio-df",
  "unnamed-strafe-wjfix",
  "kool_simple2-wjfix",
  "hoppin",
  "KTOTAM-1",
  "rek-dire-wjfix-allslick",
  "pornstar-kurkure",
  // Feature showcase examples: water + teleporters + movers + slick.
  "gdfrun",
  "geeg-tech2",
  "ghost-arcaon",
  // Classic Quake 3 Arena maps (pro variants) — the "one classic per game" pick.
  "pro-q3dm6",
  "pro-q3dm13",
  "pro-q3tourney2",
].map((m) => m.toLowerCase());
const CATALOG_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // re-fetch weekly
/** Bump together with CATALOG_KEY: adds `?v=` so the browser HTTP cache can't
 *  serve a pre-change `/catalog` response. */
const CATALOG_VERSION = 5;

let catalogPromise: Promise<MapInfo[]> | null = null;

/** Fetch (and cache) the map catalog. */
async function fetchCatalog(): Promise<MapInfo[]> {
  const res = await fetch(api(`/catalog?v=${CATALOG_VERSION}`));
  if (!res.ok) throw new Error(`catalog ${res.status}`);
  const data = (await res.json()) as Catalog;
  return data.items ?? [];
}

/**
 * Slim the catalog down to the fields the UI/search/favorites use before
 * caching it. The padpork `MapInfo` rows carry a lot of unused metadata
 * (levelshot paths, timestamps, ...); the full list is ~5 MB of JSON, which
 * blew the ~5 MB localStorage quota. A failed `setItem` used to be caught by
 * the outer `catch`, which then returned the (empty) cache — leaving the Maps
 * tab blank. Keep this shape-only copy small and treat a quota error as
 * "no cache", never as a failed load.
 */
function cacheShape(maps: MapInfo[]): MapInfo[] {
  return maps.map((m) => ({
    map_name: m.map_name,
    normalized_name: m.normalized_name ?? m.map_name,
    game: m.game ?? "",
    longname: m.longname ?? null,
    author: m.author ?? null,
    has_rocket_launcher: m.has_rocket_launcher ?? 0,
    has_plasmagun: m.has_plasmagun ?? 0,
    has_grenade_launcher: m.has_grenade_launcher ?? 0,
    has_slick: m.has_slick ?? 0,
    has_water: m.has_water ?? 0,
    has_slime: m.has_slime ?? 0,
    has_lava: m.has_lava ?? 0,
    has_fog: m.has_fog ?? 0,
    has_teleporter: m.has_teleporter ?? 0,
    has_jumppad: m.has_jumppad ?? 0,
    has_timer: m.has_timer ?? 0,
    record_count: m.record_count ?? 0,
    best_time_ms: m.best_time_ms ?? null,
    best_player_name: m.best_player_name ?? null,
    pack: m.pack,
    pack_name: m.pack_name,
    pack_description: m.pack_description,
  }));
}

export interface MapPackInfo {
  folder: string;
  name: string;
  description: string;
  author: string;
  maps: MapInfo[];
}

/** Map packs: folders under `maps/` on the server, with their maps. */
export async function getMapPacks(): Promise<MapPackInfo[]> {
  const res = await fetch(api("/maps/packs.json"), { cache: "no-store" });
  if (!res.ok) throw new Error(`packs ${res.status}`);
  const data = (await res.json()) as { packs?: MapPackInfo[] };
  return Array.isArray(data.packs) ? data.packs : [];
}

/** Load the catalog, using localStorage cache when fresh. */
export async function getCatalog(force = false): Promise<MapInfo[]> {
  if (catalogPromise) return catalogPromise;
  catalogPromise = (async () => {
    try {
      const ts = Number(localStorage.getItem(CATALOG_TS_KEY) ?? 0);
      const cached = localStorage.getItem(CATALOG_KEY);
      if (!force && cached && Date.now() - ts < CATALOG_MAX_AGE_MS) {
        return JSON.parse(cached) as MapInfo[];
      }
      const maps = await fetchCatalog();
      // A cache write must never fail the load (quota, private mode, ...).
      try {
        localStorage.setItem(CATALOG_KEY, JSON.stringify(cacheShape(maps)));
        localStorage.setItem(CATALOG_TS_KEY, String(Date.now()));
      } catch {
        /* cache unavailable — the in-memory catalog still works */
      }
      return maps;
    } catch (e) {
      // Fall back to cache if the network fetch failed.
      const cached = localStorage.getItem(CATALOG_KEY);
      if (cached) return JSON.parse(cached) as MapInfo[];
      console.warn("catalog unavailable:", e);
      // Don't memoize a failure: allow a later open to retry (e.g. the
      // map-server was restarting when the page loaded).
      catalogPromise = null;
      return [];
    }
  })();
  return catalogPromise;
}

// ---- Favorites ----

export function getFavorites(): string[] {
  try {
    const raw = localStorage.getItem(FAVORITES_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

export function isFavorite(mapName: string): boolean {
  const n = mapName.toLowerCase();
  return TESTING_MAPS.includes(n) || getFavorites().includes(n);
}

export function toggleFavorite(mapName: string): string[] {
  const name = mapName.toLowerCase();
  const favs = getFavorites();
  const idx = favs.indexOf(name);
  if (idx >= 0) favs.splice(idx, 1);
  else favs.push(name);
  localStorage.setItem(FAVORITES_KEY, JSON.stringify(favs));
  return favs;
}

// ---- Search & random ----

const KEYWORD_FLAGS: Record<string, (m: MapInfo) => boolean> = {
  slick: (m) => !!m.has_slick,
  rocket: (m) => !!m.has_rocket_launcher,
  plasma: (m) => !!m.has_plasmagun,
  grenade: (m) => !!m.has_grenade_launcher,
  teleporter: (m) => !!m.has_teleporter,
  teleport: (m) => !!m.has_teleporter,
  jumppad: (m) => !!m.has_jumppad,
  water: (m) => !!m.has_water,
  slime: (m) => !!m.has_slime,
  lava: (m) => !!m.has_lava,
  fog: (m) => !!m.has_fog,
  timer: (m) => !!m.has_timer,
};

/** The movement preset implied by a map's name prefix (or null if unknown). */
export function presetForMap(name: string): string | null {
  const n = (name || "").toLowerCase();
  if (/^(surf|tricksurf)[_-]/.test(n) || n === "tricksurf") return "surf";
  if (/^bhop[_-]/.test(n)) return "bhop";
  if (/^ahop[_-]/.test(n)) return "ahop";
  if (/^rj[_-]/.test(n)) return "rocketjump";
  if (/^sj[_-]/.test(n)) return "stickyjump";
  if (/^conc[_-]/.test(n)) return "conc";
  if (/^(climb|kz)[_-]/.test(n)) return "climb";
  if (/^df[_-]/.test(n)) return "defrag";
  return null;
}

/** Search maps by a free-text query (substring match on name/longname/author). */
export function searchMaps(maps: MapInfo[], query: string): MapInfo[] {
  const q = query.trim().toLowerCase();
  if (!q) return maps;
  return maps.filter(
    (m) =>
      m.map_name.toLowerCase().includes(q) ||
      (m.normalized_name ?? "").toLowerCase().includes(q) ||
      (m.longname ?? "").toLowerCase().includes(q) ||
      (m.author ?? "").toLowerCase().includes(q) ||
      // Source game (e.g. `quakelive` / `quake3` / `warsow`).
      (m.game ?? "").toLowerCase().includes(q),
  );
}

/** Parse a vote string like "random slick" or "mapname". */
export interface VoteRequest {
  kind: "specific" | "random" | "random-keyword";
  map?: string;
  keyword?: string;
}

export function parseVote(input: string): VoteRequest | null {
  const s = input.trim();
  if (!s) return null;
  const parts = s.split(/\s+/);
  const head = parts[0].toLowerCase();
  if (head === "random" || head === "randmap" || head === "vr") {
    if (parts.length >= 2) {
      const keyword = parts.slice(1).join(" ").toLowerCase();
      return { kind: "random-keyword", keyword };
    }
    return { kind: "random" };
  }
  if (head === "map" || head === "vote" || head === "vm") {
    const map = parts.slice(1).join(" ");
    if (!map) return null;
    return { kind: "specific", map };
  }
  // Bare map name -> specific vote.
  return { kind: "specific", map: s };
}

/** Resolve a vote request to a concrete map name (or null). */
export function resolveVote(maps: MapInfo[], req: VoteRequest): string | null {
  if (req.kind === "specific" && req.map) {
    const found = maps.find((m) => m.map_name.toLowerCase() === req.map!.toLowerCase());
    return found ? found.map_name : null;
  }
  if (req.kind === "random") {
    return randomMap(maps);
  }
  if (req.kind === "random-keyword" && req.keyword) {
    return randomMap(maps, req.keyword);
  }
  return null;
}

/** Pick a random map, optionally filtered by keyword. */
export function randomMap(maps: MapInfo[], keyword?: string): string | null {
  let pool = maps;
  if (keyword) {
    const flag = KEYWORD_FLAGS[keyword];
    if (flag) {
      pool = maps.filter(flag);
    } else {
      // Fall back to name/longname substring.
      pool = maps.filter(
        (m) =>
          m.map_name.toLowerCase().includes(keyword) ||
          (m.longname ?? "").toLowerCase().includes(keyword),
      );
    }
  }
  if (pool.length === 0) return null;
  return pool[Math.floor(Math.random() * pool.length)].map_name;
}
